// src/services/reports.service.js
import { api } from './api';

class ReportsService {
    /**
     * Get attendance report
     */
    async getAttendanceReport(params = {}) {
        try {
            const response = await api.get('/api/reports/attendance', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching attendance report:', error);
            throw error;
        }
    }

    /**
     * Get leave report
     */
    async getLeaveReport(params = {}) {
        try {
            const response = await api.get('/api/reports/leave', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching leave report:', error);
            throw error;
        }
    }

    /**
     * Get performance report
     */
    async getPerformanceReport(params = {}) {
        try {
            const response = await api.get('/api/reports/performance', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching performance report:', error);
            throw error;
        }
    }

    /**
     * Get productivity report
     */
    async getProductivityReport(params = {}) {
        try {
            const response = await api.get('/api/reports/productivity', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching productivity report:', error);
            throw error;
        }
    }

    /**
     * Export report to CSV
     */
    exportToCSV(reportType, data, summary, period) {
        const headers = this.getHeadersForReportType(reportType);
        const rows = data.map(row => this.getRowDataForReportType(reportType, row));

        // Create CSV content with summary first
        const summaryLines = [
            `Report Type: ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`,
            `Period: ${period}`,
            `Generated: ${new Date().toLocaleDateString()}`,
            ``,
            `Summary:`,
            ...this.getSummaryLinesForReportType(reportType, summary),
            ``,
            ``
        ];

        const csvContent = [
            ...summaryLines,
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportType}-report-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Get headers for report type
     */
    getHeadersForReportType(reportType) {
        switch (reportType) {
            case 'attendance':
                return ['Employee Code', 'Name', 'Department', 'Working Days', 'Present', 'Absent', 'Leave', 'Holidays', 'Avg Hours', 'Attendance %'];
            case 'leave':
                return ['Employee Code', 'Name', 'Department', 'Total Requests', 'Approved', 'Pending', 'Rejected', 'Leave Days'];
            case 'performance':
                return ['Employee Code', 'Name', 'Department', 'Designation', 'Rating', 'Active Goals', 'Completed Goals', 'Productivity'];
            case 'productivity':
                return ['Employee Code', 'Name', 'Department', 'Designation', 'Working Days', 'Present Days', 'Avg Hours', 'Attendance %', 'Productivity Score'];
            default:
                return [];
        }
    }

    /**
     * Get row data for report type
     */
    getRowDataForReportType(reportType, row) {
        switch (reportType) {
            case 'attendance':
                return [
                    row.employee_code,
                    `${row.first_name} ${row.last_name}`,
                    row.department_name || row.department || 'N/A',
                    row.total_working_days || row.working_days || 0,
                    row.present_days || 0,
                    row.absent_days || 0,
                    row.leave_days || 0,
                    row.holiday_days || 0,
                    row.avg_work_hours || '0.00',
                    row.attendance_percentage || '0.00'
                ];
            case 'leave':
                return [
                    row.employee_code,
                    `${row.first_name} ${row.last_name}`,
                    row.department || 'N/A',
                    row.total_requests || 0,
                    row.approved_requests || 0,
                    row.pending_requests || 0,
                    row.rejected_requests || 0,
                    row.total_leave_days || 0
                ];
            case 'performance':
                return [
                    row.employee_code,
                    `${row.first_name} ${row.last_name}`,
                    row.department || 'N/A',
                    row.designation || 'N/A',
                    row.rating || 'N/A',
                    row.active_goals || 0,
                    row.completed_goals || 0,
                    row.productivity || 0
                ];
            case 'productivity':
                return [
                    row.employee_code,
                    `${row.first_name} ${row.last_name}`,
                    row.department || 'N/A',
                    row.designation || 'N/A',
                    row.working_days || 0,
                    row.present_days || 0,
                    row.avg_hours_per_day || '0.00',
                    row.attendance_rate || '0.00',
                    row.productivity_score || 0
                ];
            default:
                return [];
        }
    }

    /**
     * Get summary lines for report type
     */
    getSummaryLinesForReportType(reportType, summary) {
        switch (reportType) {
            case 'attendance':
                return [
                    `Total Employees: ${summary.totalEmployees}`,
                    `Total Working Days: ${summary.totalWorkingDays || 'N/A'}`,
                    `Average Attendance: ${summary.avgAttendance}%`,
                    `Total Present Days: ${summary.totalPresent}`,
                    `Total Absent Days: ${summary.totalAbsent}`,
                    `Total Leave Days: ${summary.totalLeave}`,
                    `Total Holidays: ${summary.totalHolidays || 0}`
                ];
            case 'leave':
                return [
                    `Total Employees: ${summary.totalEmployees}`,
                    `Total Requests: ${summary.totalRequests}`,
                    `Approved: ${summary.approvedRequests}`,
                    `Pending: ${summary.pendingRequests}`,
                    `Rejected: ${summary.rejectedRequests}`,
                    `Total Leave Days: ${summary.totalLeaveDays}`
                ];
            case 'performance':
                return [
                    `Total Employees: ${summary.totalEmployees}`,
                    `Average Rating: ${summary.avgRating}`,
                    `Average Productivity: ${summary.avgProductivity}%`,
                    `Top Performers: ${summary.topPerformers}`,
                    `Needs Improvement: ${summary.needsImprovement}`
                ];
            case 'productivity':
                return [
                    `Total Employees: ${summary.totalEmployees}`,
                    `Average Productivity: ${summary.avgProductivity}%`,
                    `Average Attendance Rate: ${summary.avgAttendanceRate}%`
                ];
            default:
                return [];
        }
    }
}

export default new ReportsService();
